/* /payroll/run — 5-step payroll run: Inputs → Review → Approve → Export (WPS) → Paid.
 * The run state machine lives on the server (409 rules in API.md); this page drives it. */
import "./pay.css";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useData } from "../../data";
import {
  useAckMudad, useApproveRun, useExportWps, useImportOvertime, useMarkPaid, usePayrollRun, useSetRunStep, useSettings,
} from "../../data/hooks";
import type { PayrollRunView } from "../../data/types";
import { useI18n } from "../../i18n";
import { fmtN, gDate, moneyText, periodLabel } from "../../lib/format";
import {
  AiNote, Alert, Button, Card, Chip, DualDate, ErrorBox, Grid, HBars, Icon, Kpi, Kv, Money, PageHeader, Person, Skeleton, cx, errMsg, useToast,
} from "../../ui";
import AdjustmentsTable from "./components/AdjustmentsTable";
import { unknownEmp, useEmpMap, useRole } from "./components/common";
import { Art92Alert, MudadAlert, runStatus } from "./components/payroll-bits";
import { curL, otPct } from "../../lib/country";

export const RUN_STEPS: [string, string][] = [["Inputs", "المدخلات"], ["Review", "المراجعة"], ["Approve", "الاعتماد"], ["Export (WPS)", "التصدير (حماية الأجور)"], ["Paid", "الصرف"]];

/** Can the stepper jump to step i? (client mirror of the 409 rules). */
export function canGo(v: PayrollRunView, i: number): boolean {
  const r = v.run;
  if (r.paid) return false;
  if (i > r.step + 1) return false;
  if (i > 2 && !r.approved) return false;
  if (i > 3 && !r.exported) return false;
  return true;
}

export default function PayrollRun() {
  const { t, L, lang, name } = useI18n();
  const nav = useNavigate();
  const toast = useToast();
  const { can, role } = useRole();
  const q = usePayrollRun();
  const setQ = useSettings();
  const emps = useEmpMap();
  const stepM = useSetRunStep();
  const approveM = useApproveRun();
  const exportM = useExportWps();
  const ackM = useAckMudad();
  const paidM = useMarkPaid();
  const otM = useImportOvertime();
  const data = useData();
  const [comment, setComment] = useState("");
  const [bankRef, setBankRef] = useState("");

  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;

  const v = q.data, r = v.run, tot = v.totals, st = r.step;
  const payDay = setQ.data?.payDay ?? 27;
  const payDate = r.period + "-" + String(payDay).padStart(2, "0");
  const fail = (e: unknown) => toast(errMsg(e), "bad");
  const go = (i: number) => stepM.mutate(i, { onError: fail, onSuccess: () => window.scrollTo(0, 0) });
  const canOps = can("payroll.ops"), canApprove = can("payroll.approve");
  const empName = (id: number) => name(emps.map.get(id) ?? unknownEmp(id));

  const approve = () => approveM.mutate(undefined, {
    onSuccess: (nv) => toast(L("Payroll approved by ", "تم اعتماد الرواتب من ") + (nv.run.approvedBy ?? "")),
    onError: fail,
  });
  const exportWps = () => exportM.mutate(undefined, { onSuccess: (nv) => toast(L("WPS file generated · ", "تم إنشاء ملف حماية الأجور · ") + (nv.run.exportFile?.name ?? "")), onError: fail });
  const ack = () => ackM.mutate(undefined, { onSuccess: () => toast(L("Mudad check acknowledged", "تم تأكيد مراجعة مدد")), onError: fail });
  const markPaid = () => paidM.mutate(undefined, { onSuccess: () => toast(L(periodLabel(r.period, "en") + " payroll marked as paid · payslips sent", "تم تأكيد صرف رواتب " + periodLabel(r.period, "ar") + " وإرسال القسائم")), onError: fail });
  const importOt = () => otM.mutate(undefined, {
    onSuccess: (nv) => toast(L("Overtime imported from attendance · " + fmtN(nv.slips.reduce((a, s) => a + s.otHours, 0), 1) + " h", "تم استيراد العمل الإضافي من الحضور · " + fmtN(nv.slips.reduce((a, s) => a + s.otHours, 0), 1) + " ساعة")),
    onError: fail,
  });

  /* ---------- step bodies ---------- */
  let body: React.ReactNode = null;
  if (st === 0) {
    body = (
      <>
        <div className="aibar" data-testid="ot-import">
          <Icon name="fingerprint" />
          <div className="grow">
            <b>{L("Overtime from biometric punches", "العمل الإضافي من بصمات الحضور")}</b>
            <span className="xs muted" style={{ display: "block" }}>
              {L("StartHR reads the processed attendance for " + periodLabel(r.period, "en") + " (first-in / last-out per day from your devices, after the attendance policy's shift length and OT threshold) and fills each employee's OT hours. Bonuses, absences and deductions are kept.",
                "يقرأ ستارت HR الحضور المعالج لشهر " + periodLabel(r.period, "ar") + " (أول دخول وآخر خروج يوميًا من أجهزتك بعد طول الوردية وحد العمل الإضافي في سياسة الحضور) ويعبئ ساعات العمل الإضافي لكل موظف مع الإبقاء على المكافآت والغياب والخصومات.")}
            </span>
          </div>
          <Button icon="download" onClick={importOt} loading={otM.isPending} disabled={r.approved || !canOps} data-testid="btn-import-ot">{L("Import overtime from attendance", "استيراد العمل الإضافي من الحضور")}</Button>
        </div>
        <div className="mt">
          <Alert tone="info" tint title={L("Overtime is paid at " + otPct() + " of the hourly basic wage", "يُحتسب العمل الإضافي بنسبة " + otPct() + " من الأجر الأساسي بالساعة")}
            desc={L("Hourly basic = basic ÷ 30 ÷ 8. Loan instalments are deducted automatically. Press Enter or ✓ to save a row.", "الأجر بالساعة = الأساسي ÷ 30 ÷ 8. تُخصم أقساط القروض تلقائيًا. اضغط Enter أو ✓ لحفظ الصف.")} />
        </div>
        {r.approved && <div className="mt"><Alert tone="warn" testId="locked-alert" title={L("Run approved — inputs are locked", "المسير معتمد — المدخلات مقفلة")} desc={L("Approved runs cannot be changed.", "لا يمكن تعديل المسير بعد اعتماده.")} /></div>}
        <div className="mt"><Art92Alert view={v} emp={emps.map} okToo /></div>
        <AdjustmentsTable view={v} emp={emps.map} locked={r.approved} canEdit={canOps} />
      </>
    );
  }
  if (st === 1) {
    body = (
      <>
        <div className="g g-4 mb">
          <Kpi testId="rv-earnings" label={t("Earnings")} value={fmtN(tot.gross, 0)} unit={curL(L)} icon="wallet" />
          <Kpi testId="rv-deductions" label={t("Deductions")} value={fmtN(tot.deductions, 0)} unit={curL(L)} icon="minus" tone="bad" />
          <Kpi testId="rv-net" label={t("Net to bank")} value={fmtN(tot.net, 0)} unit={curL(L)} icon="banknote" tone="gold" />
          <Kpi testId="rv-cost" label={L("Total company cost", "إجمالي تكلفة المنشأة")} value={fmtN(tot.employerCost, 0)} unit={curL(L)} icon="building" tone="info" foot={L("incl. GOSI employer ", "يشمل تأمينات صاحب العمل ") + fmtN(tot.gosiEr, 0)} />
        </div>
        <div className="stack mb">
          <Art92Alert view={v} emp={emps.map} okToo />
          <MudadAlert issues={v.mudad} emp={emps.map} action={<span className="xs muted">{L("Acknowledge at export", "يُؤكَّد عند التصدير")}</span>} />
        </div>
        <section className="card" data-testid="review-table">
          <div className="tw">
            <table className="tbl cards compact">
              <thead><tr><th>{t("Employee")}</th><th className="end hide-md">{t("Earnings")}</th><th className="end hide-md">{L("GOSI", "التأمينات")}</th><th className="end">{t("Deductions")}</th><th className="end">{t("Net pay")}</th><th>{L("Art. 92", "م 92")}</th></tr></thead>
              <tbody>
                {v.slips.map((p) => {
                  const e = emps.map.get(p.employeeId);
                  return (
                    <tr key={p.employeeId} data-testid={"review-row-" + p.employeeId} className={p.cap.over ? "pay-over" : undefined}>
                      <td className="k1">{e ? <Link to={"/payslip/" + p.employeeId + "/" + r.period} style={{ color: "inherit", textDecoration: "none" }}><Person e={e} /></Link> : "#" + p.employeeId}</td>
                      <td className="end hide-md x" data-l={t("Earnings")}><Money value={p.earnings} plain /></td>
                      <td className="end hide-md x" data-l={L("GOSI", "التأمينات")}><Money value={p.gosi.emp} plain /></td>
                      <td className="end x" data-l={t("Deductions")}><Money value={p.deductions} plain /></td>
                      <td className="end k3" data-l={t("Net pay")}><b><Money value={p.net} /></b></td>
                      <td className="k2">{p.cap.over ? <span className="chip bad dot">{L("Over cap", "يتجاوز الحد")}</span> : <span className="xs muted num">{fmtN(p.cap.limit ? (p.cap.amount / p.cap.limit) * 50 : 0, 0)}%</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><td>{t("Total")}</td><td className="end hide-md"><Money value={tot.gross} plain /></td><td className="end hide-md"><Money value={tot.gosiEmp} plain /></td><td className="end"><Money value={tot.deductions} plain /></td><td className="end"><Money value={tot.net} /></td><td /></tr></tfoot>
            </table>
          </div>
        </section>
      </>
    );
  }
  if (st === 2) {
    const blocked = v.art92.length > 0;
    body = (
      <Grid cols="2-1">
        <Card title={L("Approval", "الاعتماد")} testId="approval-card">
          <ol className="steps">
            {[
              [L("Inputs prepared", "تجهيز المدخلات"), L(tot.headcount + " employees · " + moneyText(tot.net, "en"), tot.headcount + " موظفًا · " + moneyText(tot.net, "ar")), true],
              [L("Reviewed", "المراجعة"), v.art92.length ? L(v.art92.length + " Art. 92 issues", v.art92.length + " مخالفات م 92") : L("No Art. 92 issues", "لا توجد مخالفات م 92"), !blocked],
              [L("Approved by owner / HR", "اعتماد المالك / الموارد البشرية"), r.approved ? (r.approvedBy ?? "") + (r.approvedAt ? " · " + gDate(r.approvedAt, lang) : "") : L("Waiting", "بانتظار الاعتماد"), r.approved],
            ].map(([a, b, done], i) => (
              <li key={i} className={cx("step", done ? "done" : "cur")}>
                <span className="sd">{done ? <Icon name="check" className="s" /> : i + 1}</span><span className="st">{a}</span><span className="sm2">{b}</span>
              </li>
            ))}
          </ol>
          {blocked && <div className="mt"><Art92Alert view={v} emp={emps.map} testId="approve-block" /></div>}
          {!r.approved && !canApprove && role && <div className="mt"><Alert tone="info" tint testId="approve-role" title={L("Only the owner or HR can approve payroll", "يعتمد الرواتب المالك أو الموارد البشرية فقط")} desc={L("You can prepare and review the run; an approver will sign it off.", "يمكنك تجهيز المسير ومراجعته وسيعتمده المخوّل.")} /></div>}
          {!r.approved && canApprove && (
            <div className="mt field">
              <label className="flabel" htmlFor="run-comment"><span>{L("Comment", "ملاحظة")}</span></label>
              <textarea className="inp" id="run-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={L("Optional note for the record…", "ملاحظة اختيارية…")} />
            </div>
          )}
        </Card>
        <Card title={t("Summary")}>
          <Kv items={[[t("Net to bank"), <b key="n"><Money value={tot.net} /></b>], [L("GOSI total", "إجمالي التأمينات"), <Money key="g" value={tot.gosiEmp + tot.gosiEr} />], [t("Employees"), tot.headcount], [t("Status"), <Chip key="s" status={runStatus(r)} />]]} />
        </Card>
      </Grid>
    );
  }
  if (st === 3) {
    const banks = new Map<string, number>();
    for (const s of v.slips) { const b = emps.map.get(s.employeeId)?.bank ?? "—"; banks.set(b, (banks.get(b) ?? 0) + 1); }
    const f = r.exportFile;
    body = (
      <>
        {v.mudad.length > 0 && (
          <div className="mb">
            {r.mudadAck
              ? <Alert tone="ok" tint testId="mudad-acked" title={L("Mudad wage mismatches acknowledged", "تم تأكيد اختلافات الأجور في مدد")} />
              : <MudadAlert issues={v.mudad} emp={emps.map} action={<Button variant="sm" onClick={ack} loading={ackM.isPending} disabled={!r.exported || !canOps} title={!r.exported ? L("Export the WPS file first", "صدّر ملف حماية الأجور أولًا") : undefined} data-testid="btn-ack-mudad">{L("Mark as corrected", "تم التصحيح")}</Button>} />}
          </div>
        )}
        {v.mudad.length === 0 && !r.mudadAck && r.exported && (
          <div className="mb"><Alert tone="info" tint title={L("Confirm the Mudad upload", "أكّد الرفع إلى مدد")} desc={L("No wage mismatches found. Confirm once the file is accepted by Mudad.", "لا توجد اختلافات. أكّد بعد قبول الملف في مدد.")} action={<Button variant="sm" onClick={ack} loading={ackM.isPending} disabled={!canOps} data-testid="btn-ack-mudad">{L("Confirm", "تأكيد")}</Button>} /></div>
        )}
        <Grid cols="2-1">
          <Card title={L("WPS salary file", "ملف حماية الأجور")} testId="wps-card">
            <div className="stack">
              {f ? (
                <Alert tone="ok" tint testId="wps-ready" title={L("File generated", "تم إنشاء الملف")} desc={L(tot.headcount + " records · " + moneyText(tot.net, "en"), tot.headcount + " سجلًا · " + moneyText(tot.net, "ar"))} />
              ) : (
                <Alert tone="info" tint title={L("Generate the Mudad SIF file", "أنشئ ملف مدد")} desc={L("CSV with employee code, ID, bank, IBAN, basic, housing, other earnings, deductions and net — one row per employee.", "ملف CSV يحتوي الرقم الوظيفي والهوية والبنك والآيبان والأساسي والسكن والمستحقات الأخرى والاستقطاعات والصافي.")} />
              )}
              <Kv items={[[L("Payer", "المنشأة"), setQ.data?.tenant ? name({ nameEn: setQ.data.tenant.name, nameAr: setQ.data.tenant.nameAr }) : "—"], [L("Format", "الصيغة"), L("Mudad SIF (CSV)", "ملف مدد (CSV)")], [L("Period", "الفترة"), periodLabel(r.period, lang)]]} />
              {f && (
                <div className="pay-filerow" data-testid="wps-file">
                  <Icon name="file" />
                  <a href={data.fileUrl(f.key)} download={f.name} target="_blank" rel="noreferrer" data-testid="wps-link"><b>{f.name}</b></a>
                  <span className="xs muted num">{fmtN(f.size / 1024, 1)} KB</span>
                </div>
              )}
              <div className="row">
                <Button variant="pri" icon="download" onClick={exportWps} loading={exportM.isPending} disabled={!canOps || !r.approved} data-testid="btn-export-wps">{f ? L("Regenerate WPS file", "إعادة إنشاء الملف") : L("Generate WPS file", "إنشاء ملف حماية الأجور")}</Button>
                {f && <a className="btn" href={data.fileUrl(f.key)} download={f.name} data-testid="btn-download-wps"><Icon name="download" /><span>{t("Download WPS file")}</span></a>}
              </div>
            </div>
          </Card>
          <Card title={L("Bank split", "التوزيع حسب البنك")}>
            <HBars data={[...banks.entries()].sort((a, b) => b[1] - a[1])} />
          </Card>
        </Grid>
      </>
    );
  }
  if (st === 4) {
    body = r.paid ? (
      <section className="card card-b" data-testid="paid-done">
        <div className="empty pay-success">
          <span className="kpi-ico"><Icon name="check" /></span>
          <h4>{L(periodLabel(r.period, "en") + " payroll is paid", "تم صرف رواتب " + periodLabel(r.period, "ar"))}</h4>
          <div>{L("Payslips were published to " + tot.headcount + " employees in self-service.", "نُشرت القسائم لـ " + tot.headcount + " موظفًا في الخدمة الذاتية.")}</div>
          <div className="row" style={{ justifyContent: "center" }}>
            {v.slips[0] && <Button icon="receipt" onClick={() => nav("/payslip/" + v.slips[0].employeeId + "/" + r.period)}>{t("Payslip")}</Button>}
            <Button variant="pri" onClick={() => nav("/payroll")} data-testid="btn-back-payroll">{t("Payroll")}</Button>
          </div>
        </div>
      </section>
    ) : (
      <Grid cols="2">
        <Card title={t("Mark as paid")} testId="paid-card">
          <div className="stack">
            <Kv items={[[t("Net to bank"), <b key="n"><Money value={tot.net} /></b>], [t("Pay date"), <DualDate key="d" date={payDate} />]]} />
            <div className="field">
              <label className="flabel" htmlFor="bank-ref"><span>{L("Bank reference", "مرجع البنك")}</span></label>
              <input className="inp num" id="bank-ref" dir="ltr" value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="ARB-2026-10-…" />
            </div>
            <div className="stack sm" data-testid="paid-checks">
              {[[r.approved, L("Run approved", "المسير معتمد")], [r.exported, L("WPS file exported", "تم تصدير ملف حماية الأجور")], [r.mudadAck, L("Mudad check acknowledged", "تم تأكيد مراجعة مدد")]].map(([ok, label], i) => (
                <div className="row nw" key={i}><span className={cx("kpi-ico", !ok && "warn")} style={{ width: 26, height: 26 }}><Icon name={ok ? "check" : "alert"} className="s" /></span><span>{label}</span></div>
              ))}
            </div>
            {!r.mudadAck && <Alert tone="warn" testId="need-ack" title={L("Acknowledge the Mudad check first", "أكّد مراجعة مدد أولًا")} action={<Button variant="sm" onClick={() => go(3)}>{L("Go to export", "الانتقال للتصدير")}</Button>} />}
          </div>
        </Card>
        <Card title={L("After payment", "بعد الصرف")}>
          <div className="stack sm">
            {[L("Payslips published in self-service", "نشر القسائم في الخدمة الذاتية"), L("Loan instalments recorded", "تسجيل أقساط القروض"), L("Journal entry ready in Accounting", "القيد جاهز في المحاسبة"), L("Run added to payroll history", "إضافة المسير إلى السجل")].map((x) => <div className="row nw" key={x}><Icon name="check" className="s" />{x}</div>)}
          </div>
        </Card>
      </Grid>
    );
  }

  /* ---------- action bar ---------- */
  let next: React.ReactNode = null;
  if (st === 0 || st === 1) next = <Button variant="pri" end="chevron-right" onClick={() => go(st + 1)} loading={stepM.isPending} disabled={!canOps} data-testid="btn-next">{t("Continue")}</Button>;
  if (st === 2) {
    next = r.approved
      ? <Button variant="pri" end="chevron-right" onClick={() => go(3)} loading={stepM.isPending} data-testid="btn-next">{t("Continue")}</Button>
      : canApprove ? <Button variant="pri" icon="check" onClick={approve} loading={approveM.isPending} disabled={v.art92.length > 0} data-testid="btn-approve">{t("Approve payroll")}</Button> : null;
  }
  if (st === 3) next = <Button variant="pri" end="chevron-right" onClick={() => go(4)} loading={stepM.isPending} disabled={!r.exported} data-testid="btn-next">{t("Continue")}</Button>;
  if (st === 4 && !r.paid) next = <Button variant="pri" icon="check" onClick={markPaid} loading={paidM.isPending} disabled={!canOps || !r.exported || !r.mudadAck} data-testid="btn-mark-paid">{t("Mark as paid")}</Button>;

  return (
    <>
      <PageHeader
        back={{ to: "/payroll", label: t("Payroll") }}
        title={L(periodLabel(r.period, "en") + " payroll", "رواتب " + periodLabel(r.period, "ar"))}
        sub={<span className="row" style={{ gap: 8 }}><DualDate date={payDate} inline /> · {L(tot.headcount + " employees", tot.headcount + " موظفًا")} · <Chip status={runStatus(r)} testId="run-status" /></span>}
      />
      <ol className="wz" data-testid="run-steps" aria-label={L("Payroll run steps", "خطوات مسير الرواتب")}>
        {RUN_STEPS.map(([en, ar], i) => (
          <li key={en} className={i < st || (i === 4 && r.paid) ? "done" : i === st ? "cur" : ""} data-testid={"step-" + i} aria-current={i === st ? "step" : undefined}>
            <button type="button" onClick={() => go(i)} disabled={i === st || !canGo(v, i) || !canOps}>
              <span className="wb" /><span className="wl">{i < st || (i === 4 && r.paid) ? <Icon name="check" className="s" /> : <span className="num">{i + 1}</span>} {L(en, ar)}</span>
            </button>
          </li>
        ))}
      </ol>
      {st === 1 && v.mudad.length > 0 && <div className="mb"><AiNote>{L("AI review: " + v.mudad.map((m) => empName(m.employeeId) + " (" + m.kind + ")").join(", ") + " will be flagged by Mudad — update the registered wage before export.", "مراجعة الذكاء: سيعلّم مدد " + v.mudad.map((m) => empName(m.employeeId) + " (" + m.kind + ")").join("، ") + " — حدّث الأجر المسجل قبل التصدير.")}</AiNote></div>}
      <div data-testid={"run-body-" + st}>{body}</div>
      <div className="actionbar">
        {st > 0 && !r.paid && <Button icon="chevron-left" onClick={() => go(st - 1)} disabled={!canOps || stepM.isPending} data-testid="btn-back">{t("Back")}</Button>}
        <span className="spacer" />
        <span className="sm muted hide-md">{t("Net to bank")} <Money value={tot.net} dec={0} /></span>
        {next}
      </div>
    </>
  );
}
